import type { FunctionComponent } from 'react';
import SidebarNavLayout from '../../../../layouts/SidebarNavLayout';
import SettingsPage from '../../../../shared/settings/SettingsPage';
import { shouldDisplayOrganizationPortalTokenSettings } from '../../../../settings/shared/permissions';
import ReleaseGroupSettingsContextProvider from './context';
import type { RouterProps } from '../../../../../types';
import type { ProjectGroup } from '../../../../../../shared/model-typings';

type Props = RouterProps<{ projectGroupId: string }> & {
  //  These come from Redux and is passed down in ProjectGroupContainer
  projectGroup: ProjectGroup;
  fetchProjectGroup: (groupId: string) => Promise<void>;
};

/**
 * ReleaseGroupsSettings is the container for all of the release groups settings pages.
 */
const ReleaseGroupsSettings: FunctionComponent<Props> = ({
  children,
  location,
  params,
  projectGroup,
  fetchProjectGroup,
}) => {
  const { pathname } = location;
  const { projectGroupId } = params;

  const settingsBody = (
    <SettingsPage>
      <ReleaseGroupSettingsContextProvider
        projectGroupId={projectGroupId}
        title={projectGroup.title}
        policyId={projectGroup.policyId}
        securityPolicyId={projectGroup.securityPolicyId}
        qualityPolicyId={projectGroup.qualityPolicyId}
        publicOnPortal={projectGroup.publicOnPortal}
        onRefetchAfterSave={() => fetchProjectGroup(projectGroupId)}
      >
        {children}
      </ReleaseGroupSettingsContextProvider>
    </SettingsPage>
  );

  //  If they can't view Portal settings, there is only one UI element to display.
  const canViewPortalSettings = shouldDisplayOrganizationPortalTokenSettings();
  if (!canViewPortalSettings) {
    return (
      <div className="container padding-top-lg">
        {settingsBody}
      </div>
    );
  }

  return (
    <SidebarNavLayout>
      <SidebarNavLayout.Nav pathname={pathname}>
        <SidebarNavLayout.NavHeading>
          Settings
        </SidebarNavLayout.NavHeading>
        <SidebarNavLayout.NavItem to={`/projects/group/${projectGroupId}/settings/general`} activeKey="general">
          General
        </SidebarNavLayout.NavItem>
        <SidebarNavLayout.NavItem to={`/projects/group/${projectGroupId}/settings/portal`} activeKey="portal">
          Portal
        </SidebarNavLayout.NavItem>
      </SidebarNavLayout.Nav>
      <SidebarNavLayout.Content>
        {settingsBody}
      </SidebarNavLayout.Content>
    </SidebarNavLayout>
  );
};

export default ReleaseGroupsSettings;
